/**
 * Whether a company may still use Crewlark, and what it costs them.
 *
 * ## Why this is computed and never stored
 *
 * A trial ends at a moment when nobody is running any code. There is no
 * request, no cron, no webhook — just a timestamp going quietly into the past
 * at 3am on a Sunday. A stored `isReadOnly` column would therefore be wrong
 * from that moment until something happened to notice, and "something
 * happened to notice" is exactly the sort of job that fails silently for a
 * fortnight.
 *
 * So nothing is stored but the facts: what the subscription says, when the
 * trial ends, when dunning started, whether a human suspended them. The
 * access level is derived from those on every request. It cannot go stale
 * because it is never written down.
 *
 * ## Read-only, not locked out
 *
 * An expired company keeps its sign-in and keeps every screen. What it loses
 * is the ability to change anything. The alternative — refusing the login —
 * was rejected deliberately: a technician standing in somebody's kitchen with
 * a half-finished job does not care whose card expired, and a product that
 * takes the schedule away mid-afternoon is one nobody recommends to a friend.
 *
 * It is also better collections. A contractor who can still see six months of
 * his own records, and is told plainly that he cannot add to them until he
 * pays, pays. One locked out entirely starts wondering whether the records
 * are gone, and a frightened customer argues instead of paying.
 *
 * ## The grace period
 *
 * A failed card is usually not a decision. It is an expiry date, a fraud
 * hold, a bank that declined a foreign charge on a Sunday. Stripe retries
 * over the following couple of weeks and most of those recover on their own.
 * Going read-only on the first decline would punish a dozen people for a
 * problem eleven of them did not know they had, so `past_due` keeps full
 * access for `DUNNING_GRACE_DAYS` first.
 */
import { assertCents } from './money.js';
export const ACCESS_REASON_LABELS = {
    trialing: 'Free trial',
    trial_expired: 'Trial ended',
    subscribed: 'Subscribed',
    payment_failed_grace: 'Payment failed',
    payment_failed: 'Payment overdue',
    canceled: 'Cancelled',
    suspended: 'Suspended',
};
/** How long a failed payment keeps full access while Stripe retries. */
export const DUNNING_GRACE_DAYS = 7;
/** How long a new company gets for nothing. */
export const TRIAL_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
function asDate(value) {
    if (value == null)
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * What this company may do right now.
 *
 * `now` is a parameter rather than a call to `Date.now()` inside, so that the
 * admin screen can answer "what will this look like on Friday" with the same
 * function that decides it, and so the tests are not a lecture about mocking
 * clocks.
 */
export function companyAccess(facts, now) {
    const suspendedAt = asDate(facts.suspendedAt);
    /*
     * First, and without consulting anything else. A suspension is a decision
     * somebody made on purpose — unpaid invoice, abuse, a customer who asked to
     * be switched off — and it must not be undone by a subscription that still
     * looks healthy underneath it.
     */
    if (suspendedAt && suspendedAt <= now) {
        return { access: 'read_only', reason: 'suspended', changesAt: null };
    }
    switch (facts.status) {
        case 'trialing': {
            const endsAt = asDate(facts.trialEndsAt);
            /*
             * No end date on a trial means somebody created the company without
             * one. Treated as expired rather than as forever: an account that
             * silently never ends is a bug that only shows up as missing revenue,
             * and missing revenue is invisible. The admin page can extend it in a
             * click, and at least somebody will ask.
             */
            if (!endsAt)
                return { access: 'read_only', reason: 'trial_expired', changesAt: null };
            if (endsAt > now)
                return { access: 'full', reason: 'trialing', changesAt: endsAt };
            return { access: 'read_only', reason: 'trial_expired', changesAt: null };
        }
        case 'active':
            return { access: 'full', reason: 'subscribed', changesAt: null };
        case 'past_due': {
            const since = asDate(facts.pastDueSince);
            /*
             * Without a start date the grace cannot be measured, and the safe
             * reading is the generous one: Stripe said past_due, we do not know
             * when, and locking somebody out on a guess is worse than carrying them
             * for a week. The dunning emails are doing the work either way.
             */
            if (!since)
                return { access: 'full', reason: 'payment_failed_grace', changesAt: null };
            const graceEnds = new Date(since.getTime() + DUNNING_GRACE_DAYS * DAY_MS);
            if (graceEnds > now) {
                return { access: 'full', reason: 'payment_failed_grace', changesAt: graceEnds };
            }
            return { access: 'read_only', reason: 'payment_failed', changesAt: null };
        }
        case 'canceled':
            return { access: 'read_only', reason: 'canceled', changesAt: null };
    }
}
/** Shorthand for the common question. */
export function isReadOnly(facts, now) {
    return companyAccess(facts, now).access === 'read_only';
}
// --- what it costs -----------------------------------------------------------
/** The monthly price, including the first `PLAN_INCLUDED_SEATS` users. */
export const PLAN_BASE_CENTS = 9900;
/** Users included in the base price. */
export const PLAN_INCLUDED_SEATS = 3;
/** Each user past the included ones. */
export const PLAN_SEAT_CENTS = 2500;
/**
 * How many seats are actually charged for.
 *
 * Seats below the included count are not negative money. A two-person company
 * pays the base price and no more; it does not earn a credit for the third
 * seat it has not filled.
 */
export function billableSeats(seats) {
    if (!Number.isFinite(seats))
        return 0;
    return Math.max(0, Math.floor(seats) - PLAN_INCLUDED_SEATS);
}
/**
 * The monthly bill for a company with this many users.
 *
 * Whole cents, computed from integers, because this is the number that
 * appears on a card statement and gets queried.
 */
export function monthlyTotalCents(seats) {
    return assertCents(PLAN_BASE_CENTS + billableSeats(seats) * PLAN_SEAT_CENTS);
}
/** When a trial started now would end. */
export function trialEndFrom(start, days = TRIAL_DAYS) {
    return new Date(start.getTime() + days * DAY_MS);
}
/**
 * Push a trial out by `days` from wherever it stands.
 *
 * Measured from the later of its current end and now, which is the behaviour
 * somebody clicking "extend by 7 days" expects: extending a trial that ran
 * out a month ago should give them seven days from today, not seven days that
 * were used up in September.
 */
export function extendTrial(currentEnd, days, now) {
    const end = asDate(currentEnd);
    const from = end && end > now ? end : now;
    return new Date(from.getTime() + days * DAY_MS);
}
//# sourceMappingURL=subscription.js.map